import { useEffect, useState } from 'react';

import router from 'next/router';
import ScrollToTop from 'react-scroll-to-top';

import { SeoHead } from '@components/SeoHead';
import SearchBase from '@components/base/SearchBase';
import { MainModal } from '@components/modal/Modal';
import HadithResultItem from '@components/results/items/HadithResultItem';
import { getBookAndIdText } from '@components/results/items/helpers';
import MySnackbar, {
    type MySnackbarProps,
} from '@components/snackbar/Snackbar';
import { getIncludeFields } from '@components/views/MainSearchViews/function';

import { ReactiveComponent } from '@appbaseio/reactivesearch';
import { narrationsIndex, narratorsIndex } from '@config/reactive-search';
import { HadithViewFilters } from '@constants/filters';
import { useContextualRouting } from '@hooks';
import useParams, { ROUTING } from '@hooks/useParams';
import { PageLayout } from '@layout';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import {
    Box,
    CircularProgress,
    Paper,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { addAmbiguousSpan } from '@utils/addAmbiguousSpan';

import { HadithController, NarratorsController } from 'components';
import { NarrationsDataFields } from 'constants/datafields';
import {
    NarratorsPageNarrator,
    SearchPageHadith,
} from 'shared/interfaces/hadith';
import { colors } from 'theme';
import { z } from 'zod';

export const Result = z.object({
    data: z.object({
        hits: z.object({
            total: z.object({ value: z.number() }),
            hits: z
                .object({
                    _source: NarratorsPageNarrator,
                })
                .array(),
        }),
    }),
});
export type Result = z.infer<typeof Result>;

const NarratorsPage = () => {
    const scrollToTopMin =
        typeof window !== 'undefined' ? window.innerHeight * 3 : undefined;
    const [narrators, setNarrators] = useState<NarratorsPageNarrator[]>([]);
    const [numNarrators, setNumNarrators] = useState(0);
    const [showSnackbar, setShowSnackbar] = useState<MySnackbarProps>({
        message: '',
        show: false,
    });

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [loading, setLoading] = useState(true);

    const [hadith, setHadith] = useState<SearchPageHadith>();
    const { hadithId } = useParams();
    const { returnHref } = useContextualRouting();

    useEffect(() => {
        fetch('/api/narrators')
            .then((res) => res.json())
            .then((json) => {
                const {
                    data: {
                        hits: {
                            total: { value },
                            hits,
                        },
                    },
                } = Result.parse(json);

                const loading = hits.length === 0;
                const hitsSource = hits.map((hit) => hit._source);

                setNumNarrators(value);
                setLoading(loading);
                setNarrators(hitsSource);
            })
            .catch((err) => {
                console.error('error fetching full narrators list', err);
            });
    }, []);

    return (
        <>
            <SeoHead />
            <PageLayout>
                <Paper
                    elevation={0}
                    variant={isMobile ? 'elevation' : 'outlined'}
                    sx={{
                        padding: { xs: '10px', md: '20px' },
                        borderRadius: '10px',
                        height: 'fit-content',
                    }}
                >
                    <SearchBase app={narratorsIndex}>
                        <NarratorsController
                            autoComplete
                            tabs
                            narrators={narrators}
                            numNarrators={numNarrators}
                            routing={ROUTING.NARRATOR_PAGE}
                            setShowSnackbar={setShowSnackbar}
                            loading={loading}
                        />
                    </SearchBase>
                </Paper>
                <SearchBase
                    app={narrationsIndex}
                    // NOTE: Very important to use this prop to call getServerSideProps.  Don't use getSearchParams props as it will not read filters from the search query
                    setSearchParams={(newURL) => {
                        router.push(newURL, undefined, { shallow: true });
                    }}
                >
                    {!!hadithId && (
                        <MainModal
                            handleClose={() => {
                                router
                                    .push(returnHref, undefined, {
                                        shallow: true,
                                    })
                                    .then(() => setHadith(undefined));
                            }}
                            moveTitleToRight={true}
                            title={
                                isMobile
                                    ? hadith
                                        ? getBookAndIdText(
                                              hadith.book_name,
                                              hadith.number[0],
                                          )
                                        : ''
                                    : ''
                            }
                        >
                            <Paper
                                elevation={0}
                                variant={isMobile ? 'elevation' : 'outlined'}
                                sx={{
                                    border: 'none',
                                    height: 'fit-content',
                                }}
                            >
                                <ReactiveComponent
                                    componentId={HadithViewFilters.HADITH_ID}
                                    defaultQuery={() => ({
                                        _source: getIncludeFields(),
                                        query: {
                                            term: {
                                                [NarrationsDataFields.HADITH_ID]:
                                                    {
                                                        value: hadithId?.toString(),
                                                    },
                                            },
                                        },
                                    })}
                                    onData={(response) => {
                                        const parsedResponse = response;
                                        addAmbiguousSpan(
                                            parsedResponse.data[0],
                                        );
                                        setHadith(parsedResponse.data[0]);
                                    }}
                                />
                                <Box
                                    display={'flex'}
                                    alignItems={'center'}
                                    justifyContent={'center'}
                                    flexDirection={'column'}
                                >
                                    {!!hadith ? (
                                        <Box width="100%">
                                            <Box
                                                sx={{
                                                    display: {
                                                        xs: 'none',
                                                        md: 'block',
                                                    },
                                                }}
                                            >
                                                <HadithResultItem
                                                    hadith_object={hadith}
                                                    rightActions={[]}
                                                    setShowSnackbar={
                                                        setShowSnackbar
                                                    }
                                                />
                                            </Box>
                                            <HadithController
                                                hadith={hadith}
                                                setShowSnackbar={
                                                    setShowSnackbar
                                                }
                                            />
                                        </Box>
                                    ) : (
                                        <CircularProgress color="secondary" />
                                    )}
                                </Box>
                            </Paper>
                        </MainModal>
                    )}
                </SearchBase>

                <ScrollToTop
                    smooth
                    top={scrollToTopMin}
                    component={<KeyboardArrowUpIcon sx={{ color: 'white' }} />}
                    style={{
                        backgroundColor: colors.secondary,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bottom: '80px',
                    }}
                />
                <MySnackbar
                    anchorOrigin={{
                        vertical: isMobile ? 'top' : 'bottom',
                        horizontal: isMobile ? 'center' : 'right',
                    }}
                    message={showSnackbar.message}
                    key="top-center"
                    sx={{ mx: { xs: '20%', sm: 'unset' } }}
                    open={showSnackbar.show}
                    onClose={() =>
                        setShowSnackbar({ message: '', show: false })
                    }
                    autoHideDuration={3000}
                    severity={showSnackbar.severity}
                />
            </PageLayout>
        </>
    );
};

export default NarratorsPage;
